import { action, computed, observable, runInAction } from 'mobx'
import { registerSW } from 'virtual:pwa-register' //eslint-disable-line import/no-unresolved
import axios from 'axios'
import { APP_UPDATE_CHECK_INTERVAL } from '@/constants/app.js'
import Scheduler from '@/utils/Scheduler.js'
import { mdiUpdate } from '@mdi/js'
import { getToastIconComponent } from '@/components/ui/sonner.jsx'

export default class AppStore {
  rootStore = null

  #version = __BUILD_VERSION__ // eslint-disable-line no-undef
  #swReload = async () => {}
  #swUrl = ''
  #swReg = null
  #abortController = null
  #scheduler = null
  #toastId = null
  #isStandalone =
    navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches

  @observable accessor isInitialized = false
  @observable accessor isOnline = navigator.onLine
  @observable accessor isCheckingForUpdates = false
  @observable accessor isUpdating = false
  @observable accessor isReadyToReload = false
  @observable accessor isReloading = false

  constructor(rootStore) {
    this.rootStore = rootStore
    this.rootStore.registerDeconstructor(this.#_deconstructor)

    if (window.location.pathname.startsWith('/onramper')) return

    window.addEventListener('online', this.#onConnectionStatusChanged, { passive: true })
    window.addEventListener('offline', this.#onConnectionStatusChanged, { passive: true })
    if (this.isStandalone)
      document.addEventListener('contextmenu', this.#onContextMenu, { capture: true })

    this.#scheduler = new Scheduler(this.checkForUpdates, APP_UPDATE_CHECK_INTERVAL, true)
    this.#swReload = registerSW({
      onNeedRefresh: this.#onNeedRefresh,
      onOfflineReady: this.#onOfflineReady,
      onRegisterError: this.#onRegisterError,
      onRegisteredSW: this.#onRegisteredSW,
    })
  }

  #_deconstructor = () => {
    this.#scheduler.deconstructor()
    this.#abortController?.abort()
    this.#swReg?.removeEventListener('updatefound', this.#onUpdateFound)

    window.removeEventListener('online', this.#onConnectionStatusChanged)
    window.removeEventListener('offline', this.#onConnectionStatusChanged)
    document.removeEventListener('contextmenu', this.#onContextMenu)
  }

  get notifications() {
    return this.rootStore.notificationsStore
  }

  get isStandalone() {
    return this.#isStandalone
  }

  get isBrowser() {
    return !this.#isStandalone
  }

  get version() {
    return this.#version
  }

  @computed
  get isOffline() {
    return !this.isOnline
  }

  @computed
  get areUpdatesAvailable() {
    return this.isUpdating || this.isReadyToReload || this.isReloading
  }

  checkForUpdates = async e => {
    if (this.isCheckingForUpdates || this.isOffline || this.areUpdatesAvailable) return

    const isManualCheck = Boolean(e)

    runInAction(() => {
      this.isCheckingForUpdates = true
    })
    if (isManualCheck) this.#toastCheckingForUpdates()
    console.log('[App] Checking for updates...')

    try {
      this.#abortController = new AbortController()
      const resp = await axios.get(this.#swUrl, {
        headers: { Cache: 'no-store', 'Cache-Control': 'no-cache' },
        signal: this.#abortController.signal,
      })

      if (resp.status !== 200) return

      await this.#swReg.update()
      if (this.#swReg.installing || this.#swReg.waiting || this.areUpdatesAvailable) return

      console.log('[App] No updates available')
      this.#toastLatestVersion()
    } catch (e) {
      console.error('[App] Checking for updates failed', e)
      this.#toastUpdateError()
    } finally {
      runInAction(() => {
        this.isCheckingForUpdates = false
      })
    }
  }

  reloadToApplyUpdates = async () => {
    if (!this.isReadyToReload || this.isReloading) return

    console.log('[App] Applying updates...')
    runInAction(() => {
      this.isReloading = true
    })

    try {
      await this.#swReload()

      await new Promise(resolve => {
        if (navigator.serviceWorker?.controller) {
          const onControllerChange = () => {
            navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
            resolve()
          }

          navigator.serviceWorker.addEventListener('controllerchange', onControllerChange, {
            once: true,
          })

          setTimeout(() => {
            if (!navigator.serviceWorker) resolve()
          }, 100)
        } else resolve()
      })
    } catch (e) {
      console.error('[App] SW reload failed, forcing page reload', e)
    }

    try {
      const url = new URL(window.location.href)
      url.searchParams.set('_r', String(Date.now()))
      window.location.replace(url.toString())
    } catch {
      window.location.reload()
    }
  }

  @action
  #onConnectionStatusChanged = () => {
    this.isOnline = navigator.onLine
  }

  #onContextMenu = e => {
    e.preventDefault()
  }

  @action
  #onNeedRefresh = () => {
    if (this.isReadyToReload) return

    console.log('[App] Reload needed')
    this.isReadyToReload = true
    this.isUpdating = false

    if (!this.isInitialized) this.reloadToApplyUpdates().then()
    else this.#toastCanReloadToApplyUpdates()
  }

  @action
  #onUpdateFound = () => {
    if (this.isUpdating) return

    console.log('[App] Updating...')
    this.isUpdating = true
    this.#toastApplyingUpdates()
    this.#scheduler.stop()
  }

  @action
  #onOfflineReady = () => {
    console.log('[App] Initial install, offline mode ready')
    this.isUpdating = false
    this.isInitialized = true
  }

  #onRegisteredSW = async (swUrl, swReg) => {
    console.log('[App] Registered service worker')

    if (!swReg) throw new Error('[App] Missing service worker registration')

    this.#swUrl = swUrl
    this.#swReg = swReg
    this.#swReg.addEventListener('updatefound', this.#onUpdateFound, { passive: true })

    await this.#scheduler.start(true)

    if (!this.#swReg.installing && !this.#swReg.waiting && !this.areUpdatesAvailable) {
      console.log('[App] Ready')
      runInAction(() => {
        this.isInitialized = true
      })
    }
  }

  #onRegisterError = e => {
    throw new Error(`[App] Failed to register service worker: ${e}`)
  }

  // FIXME: don't want these notifications here but no good place for them atm
  #toastCheckingForUpdates = () => {
    if (this.#toastId) return

    this.#toastId = this.notifications.toastLoading('Checking for updates...')
  }

  #toastLatestVersion = () => {
    if (!this.#toastId) return

    this.notifications.toastSuccess('You are running the latest version.', { id: this.#toastId })
    this.#toastId = null
  }

  #toastUpdateError = () => {
    if (!this.#toastId) return

    this.notifications.toastError('Error trying to update!', { id: this.#toastId })
    this.#toastId = null
  }

  #toastApplyingUpdates = () => {
    if (!this.#toastId || !this.isUpdating) return

    this.notifications.toastLoading('Applying updates...', { id: this.#toastId })
  }

  #toastCanReloadToApplyUpdates = () => {
    if (!this.isInitialized || !this.isReadyToReload) return

    const config = {
      action: {
        label: 'Reload',
        onClick: this.reloadToApplyUpdates,
      },
      cancel: {
        label: 'Later',
      },
      description: 'Reload to enjoy the latest version of ProMeet.',
      dismissable: true,
      duration: Infinity,
      icon: getToastIconComponent(mdiUpdate),
    }

    if (this.#toastId) config.id = this.#toastId

    this.notifications.toast('Updates are ready!', config)
    this.#toastId = null
  }
}
